import { AdvancedMarker, APIProvider, Map } from "@vis.gl/react-google-maps";
import PageLayout from "@/components/custom/PageLayout";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@/redux/store";
import { GOOGLE_MAPS_API_KEY } from "@/constants";
import HubMarker from "@/assets/icons/HubMarker.svg";
import DriverCar from "@/assets/icons/DriverCar.png";
import { useEffect, useCallback, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ChevronLeftIcon, ScanEyeIcon, ChevronRightIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import i18n from "@/lang/i18n";
import LoginFlowService from "@/services/api/LoginFlowService";
import {
  CreateLoginTokenResponse,
  HubsInfo,
} from "@/central-atoms/everest-fleet-pwa/responses";
import ErrorMessageFullCard from "@/components/custom/ErrorMessageFullCard";
import { fetchDriverDetails, setHubsOfCity } from "@/redux/driverDetailsSlice";
import { useTheme } from "@/themeConfigs/ThemeContext";
import { Oval } from "react-loader-spinner";

export const LoginFlow = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { theme } = useTheme();

  const driverDetails = useSelector(
    (state: RootState) => state.driverDetails.details?.data
  );

  // Ensure hubsOfCity is always an array to avoid undefined errors
  const hubsOfCity = useSelector(
    (state: RootState) => state.driverDetails.hubsOfCity?.data || []
  );

  const [userPosition, setUserPosition] = useState({ lat: 0, lng: 0 });
  const [center, setCenter] = useState({ lat: 0, lng: 0 });
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [selectedHub, setSelectedHub] = useState<HubsInfo | null>(null);
  const [showHubSelection, setShowHubSelection] = useState(true);
  const [isLoading, setIsLoading] = useState({
    hubs: true,
    confirmation: false,
  });

  const driverId = useMemo(
    () =>
      driverDetails?.user?.additional_props?.external_user_props?.driver_id ||
      "",
    [driverDetails]
  );

  const handleGeolocationError = useCallback(
    (error: GeolocationPositionError) => {
      console.error("Geolocation error:", error);
      setErrorMessage(
        i18n.t(error.code === 1 ? "geolocation Denied" : "geolocation Error")
      );
    },
    []
  );

  const updateCenterPosition = useCallback(() => {
    if (!selectedHub) {
      setCenter(userPosition);
      return;
    }
    setCenter({
      lat: (userPosition.lat + selectedHub.data.latitude) / 2,
      lng: (userPosition.lng + selectedHub.data.longitude) / 2,
    });
  }, [userPosition, selectedHub]);

  const fetchHubsOfCity = useCallback(async () => {
    if (!driverDetails?.user) return;

    try {
      const hubsOfCityList = await LoginFlowService.getHubsOfCity();
      if (!hubsOfCityList?.data?.length) {
        throw new Error("No hubs available");
      }

      dispatch(setHubsOfCity(hubsOfCityList));
    } catch (err: any) {
      setErrorMessage(err.message || i18n.t("hubs_fetch_error"));
    } finally {
      setIsLoading((prev) => ({ ...prev, hubs: false }));
    }
  }, [driverDetails, dispatch]);

  const handleLoginConfirmation = useCallback(async () => {
    if (!selectedHub || isLoading.confirmation) return;

    setIsLoading((prev) => ({ ...prev, confirmation: true }));

    try {
      await LoginFlowService.createLogin({
        lat: userPosition.lat,
        lng: userPosition.lng,
        phone: driverDetails?.user?.phone || "",
        et_id: driverId,
        hub: selectedHub.name,
      });
      // Pull the fresh token / button state before the home screen renders it
      await dispatch(fetchDriverDetails({ force: true }));
      navigate("/driver-home");
    } catch (err: any) {
      const errorData = err.response?.data as CreateLoginTokenResponse;
      setErrorMessage(errorData?.data?.messages?.[0] || i18n.t("login_error"));
    } finally {
      setIsLoading((prev) => ({ ...prev, confirmation: false }));
    }
  }, [
    selectedHub,
    userPosition,
    driverDetails,
    driverId,
    navigate,
    dispatch,
    isLoading.confirmation,
  ]);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setUserPosition({ lat: coords.latitude, lng: coords.longitude });
        fetchHubsOfCity();
      },
      handleGeolocationError,
      { maximumAge: 20000, timeout: 20000 }
    );
  }, [fetchHubsOfCity, handleGeolocationError]);

  useEffect(() => {
    if (hubsOfCity.length === 1) {
      setSelectedHub(hubsOfCity[0]);
      setShowHubSelection(false);
    }
  }, [hubsOfCity]);

  useEffect(() => {
    if (userPosition.lat && selectedHub) {
      updateCenterPosition();
    }
  }, [userPosition, selectedHub, updateCenterPosition]);

  return (
    <PageLayout noPad>
      <div className="h-full relative">
        {errorMessage && (
          <ErrorMessageFullCard
            errorString={[errorMessage]}
            handleBackToHome={() => navigate("/driver-home")}
          />
        )}

        {!errorMessage && hubsOfCity.length > 0 && showHubSelection && (
          <div className="absolute inset-0 bg-white z-20 p-6 overflow-y-auto rounded-lg shadow-lg">
            <Button
              variant="icon"
              className="absolute p-0 top-[22px] left-[16px] z-10 text-secondary rounded-[15px] border-2 border-secondary hover:shadow-lg h-[46px] w-[46px] flex items-center justify-center"
              onClick={() => navigate("/driver-home")}
            >
              <ChevronLeftIcon />
            </Button>

            <h2 className="text-xl font-bold mt-14 mb-6 text-center text-gray-900">
              Select a Hub
            </h2>

            <div className="space-y-4">
              {hubsOfCity.map((hub) => (
                <div
                  key={hub.name}
                  onClick={() => {
                    setSelectedHub(hub);
                    setShowHubSelection(false);
                  }}
                  className="p-4 border border-gray-300 rounded-lg cursor-pointer flex items-center justify-between bg-white 
                   hover:bg-gray-100 hover:shadow-md active:shadow-lg transition-all duration-300 ease-in-out 
                   transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  <div className="font-semibold text-gray-800">{hub.name}</div>
                  <ChevronRightIcon className="h-5 w-5 text-gray-500 transition-all duration-200 group-hover:translate-x-1" />
                </div>
              ))}
            </div>
          </div>
        )}

        {!errorMessage && !showHubSelection && (
          <>
            <Button
              variant="icon"
              className="absolute p-0 top-[22px] left-[16px] z-10 text-secondary rounded-[15px] border-2 border-secondary hover:shadow-lg h-[46px] w-[46px] flex items-center justify-center"
              onClick={() => navigate("/driver-home")}
            >
              <ChevronLeftIcon />
            </Button>
            <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
              <Map
                onCenterChanged={({ detail: { center } }) => setCenter(center)}
                disableDefaultUI
                center={center}
                defaultZoom={8}
                className="h-full w-full"
                mapId="2509"
              >
                {userPosition.lat !== 0 && (
                  <AdvancedMarker position={userPosition}>
                    <img
                      src={DriverCar}
                      alt="User Position"
                      className="h-11 w-11"
                    />
                  </AdvancedMarker>
                )}
                {selectedHub && (
                  <AdvancedMarker
                    position={{
                      lat: selectedHub.data.latitude,
                      lng: selectedHub.data.longitude,
                    }}
                  >
                    <img
                      src={HubMarker}
                      alt="Hub Position"
                      className="h-12 w-12"
                    />
                  </AdvancedMarker>
                )}
              </Map>
            </APIProvider>

            <Button
              variant="secondary"
              disabled={isLoading.confirmation}
              className={cn(
                "absolute z-10 w-11/12 bottom-[50px] left-1/2 -translate-x-1/2 h-[47px] text-[14px] rounded-[10px]",
                {
                  "opacity-50 cursor-not-allowed": isLoading.confirmation,
                }
              )}
              onClick={handleLoginConfirmation}
            >
              {isLoading.confirmation
                ? i18n.t("processing")
                : i18n.t("confirm_location")}
            </Button>
          </>
        )}

        {isLoading.hubs && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Oval
              height={70}
              width={70}
              color="#ffffff"
              secondaryColor="#cccccc"
            />
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default LoginFlow;
